import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import moment from 'moment';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import ReactNumeric from 'react-numeric';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useGetUser } from '../../../../Hooks/CustomHooks';
import { GetDetalleGarantiaLocatario, SaveDetalleGarantia, UploadAdjuntoDetalleGarantia, GetBancoListHabilitadosxCartaFianza } from '../../../../api/Api';
import { Loading } from '../../../../Components/Loading/Loading';
import UploadZone from '../../../../Components/Upload/UploadZone';
import cogoToast from 'cogo-toast';

registerLocale('es', es);
const animatedComponents = makeAnimated();

const tipoCartaFianzaList = [
    { value: 383, label: 'Primera' },
    { value: 384, label: 'Renovación' }
];

export default function EditarGarantiaModal({ showModal, setShowModal, idDetalleGarantia, onSuccess }) {
    const user = useGetUser();
    const queryClient = useQueryClient();
    const [garantiaData, setGarantiaData] = useState(null);
    const [urlAdjunto, setUrlAdjunto] = useState('');
    const [isLoadingAdjunto, setIsLoadingAdjunto] = useState(false);

    const { data: bancosData, isLoading: isLoadingBancos } = useQuery(
        "getBancoListHabilitadosEdicion",
        GetBancoListHabilitadosxCartaFianza,
        {
            enabled: showModal
        }
    );

    const bancoOptions = useMemo(() => {
        if (!bancosData || !bancosData.data) return [];
        return bancosData.data.map(b => ({
            value: b.id_banco,
            label: b.codigo
        }));
    }, [bancosData]);

    const {
        isFetching: isFetchingData,
        refetch: refetchGarantia
    } = useQuery(
        ['getDetalleGarantiaLocatarioEdicion', idDetalleGarantia],
        () => GetDetalleGarantiaLocatario(idDetalleGarantia),
        {
            enabled: !!idDetalleGarantia && showModal,
            onSuccess: (res) => {
                if (res && res.data) {
                    setGarantiaData(res.data);
                    setUrlAdjunto(res.data.detalle_garantia.url_filename_carta_fianza || '');
                    
                    formik.setValues({
                        nroCartaFianza: res.data.detalle_garantia.nro_carta_fianza || '',
                        tipoCartaFianza: tipoCartaFianzaList.find(t => t.value === res.data.detalle_garantia.id_tipo_carta_fianza) || null,
                        importe: res.data.detalle_garantia.importe || '',
                        endDate: res.data.detalle_garantia.fecha_vencimiento_carta_fianza ? new Date(res.data.detalle_garantia.fecha_vencimiento_carta_fianza) : null,
                        banco: res.data.detalle_garantia.banco ? { value: res.data.detalle_garantia.banco.id_banco, label: res.data.detalle_garantia.banco.codigo } : null
                    });
                }
            },
            onError: (err) => {
                cogoToast.error('Error al cargar los datos de la garantía: ' + err.message);
            }
        }
    );

    const {
        mutate: mutateSaveDetalleGarantia,
        isLoading: isLoadingSave
    } = useMutation("saveDetalleGarantiaEdicion", SaveDetalleGarantia, {
        onSuccess: (res) => {
            if (res?.data?.success || res?.status === 200) {
                // Optimistic cache update for getDetalleGarantiasListToEdicion query
                queryClient.setQueryData("getDetalleGarantiasListToEdicion", (oldData) => {
                    if (!oldData || !oldData.data) return oldData;
                    return {
                        ...oldData,
                        data: oldData.data.map(item => {
                            if (item.detalle_garantia.id_garantia_detalle === idDetalleGarantia) {
                                return {
                                    ...item,
                                    detalle_garantia: {
                                        ...item.detalle_garantia,
                                        nro_carta_fianza: formik.values.nroCartaFianza || null,
                                        id_tipo_carta_fianza: formik.values.tipoCartaFianza ? formik.values.tipoCartaFianza.value : null,
                                        descripcion_tipo_carta_fianza: formik.values.tipoCartaFianza ? formik.values.tipoCartaFianza.label : null,
                                        importe: formik.values.importe ? parseFloat(formik.values.importe) : null,
                                        fecha_vencimiento_carta_fianza: formik.values.endDate ? moment(formik.values.endDate).toISOString() : null,
                                        url_filename_carta_fianza: urlAdjunto || item.detalle_garantia.url_filename_carta_fianza,
                                        banco: formik.values.banco ? {
                                            ...item.detalle_garantia.banco,
                                            id_banco: formik.values.banco.value,
                                            codigo: formik.values.banco.label
                                        } : null
                                    }
                                };
                            }
                            return item;
                        })
                    };
                });

                cogoToast.success('Garantía guardada exitosamente.', { position: 'top-right' });
                setShowModal(false);
                if (onSuccess) onSuccess();
            } else {
                cogoToast.error(res?.data?.message || 'Error al guardar la garantía.');
            }
        },
        onError: (err) => {
            cogoToast.error('Error al guardar la garantía: ' + err.message);
        }
    });

    const formik = useFormik({
        initialValues: {
            nroCartaFianza: '',
            tipoCartaFianza: null,
            importe: '',
            endDate: null,
            banco: null
        },
        onSubmit: (values) => {
            if (!garantiaData) return;

            const obj = {
                "id_garantia_detalle": garantiaData.detalle_garantia.id_garantia_detalle,
                "razon_social": garantiaData.garantia.razon_social,
                "nombre_comercial": garantiaData.garantia.nombre_comercial,
                "id_banco": values.banco ? values.banco.value : null,
                "importe": values.importe ? parseFloat(values.importe) : null,
                "nro_carta_fianza": values.nroCartaFianza || null,
                "nro_operacion": garantiaData.detalle_garantia.nro_operacion || null,
                "fecha_emision_carta_fianza": garantiaData.detalle_garantia.fecha_emision_carta_fianza,
                "fecha_vencimiento_carta_fianza": values.endDate ? moment(values.endDate).toISOString() : null,
                "updated_by": user.email,
                "excepcion": garantiaData.detalle_garantia.is_excepcion ? garantiaData.detalle_garantia.excepcion : null,
                "id_tipo_carta_fianza": values.tipoCartaFianza ? values.tipoCartaFianza.value : null
            };

            mutateSaveDetalleGarantia(obj);
        }
    });

    const customDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length === 0 || !garantiaData) return;
        setIsLoadingAdjunto(true);
        const formData = new FormData();
        formData.append('file', acceptedFiles[0]);

        UploadAdjuntoDetalleGarantia(formData, garantiaData.detalle_garantia.id_garantia_detalle)
            .then(response => {
                if (response?.data?.data?.url) {
                    setUrlAdjunto(response.data.data.url);
                    cogoToast.success('Archivo adjunto PDF subido correctamente.');
                } else {
                    cogoToast.error('Error al subir el archivo.');
                }
            })
            .catch(error => {
                console.error(error);
                cogoToast.error('Error al subir el archivo adjunto: ' + error.message);
            })
            .finally(() => {
                setIsLoadingAdjunto(false);
            });
    }, [garantiaData]);

    const handleClose = () => {
        setShowModal(false);
    };

    return (
        <Modal show={showModal} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Editar Detalles de Garantía</Modal.Title>
            </Modal.Header>
            <Modal.Body className="position-relative">
                {isFetchingData && (
                    <div className="d-flex justify-content-center align-items-center position-absolute w-100 h-100 bg-white bg-opacity-75" style={{ zIndex: 10, top: 0, left: 0 }}>
                        <Loading />
                    </div>
                )}
                
                {garantiaData && (
                    <Form onSubmit={formik.handleSubmit}>
                        {/* Context Read-Only Section */}
                        <div className="row bg-light rounded p-4 mb-4 g-3">
                            <div className="col-md-6">
                                <strong>Razón Social: </strong> {garantiaData.garantia.razon_social}
                            </div>
                            <div className="col-md-6">
                                <strong>Contrato: </strong> {garantiaData.garantia.nro_contrato}
                            </div>
                            <div className="col-md-6">
                                <strong>Moneda: </strong> {garantiaData.garantia.moneda.codigo}
                            </div>
                        </div>

                        {/* Editable Section */}
                        <div className="row g-4">
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-bold">Nro Carta Fianza</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="nroCartaFianza"
                                        value={formik.values.nroCartaFianza}
                                        onChange={formik.handleChange}
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-bold">Tipo Carta Fianza</Form.Label>
                                    <Select
                                        classNamePrefix='filter'
                                        components={animatedComponents}
                                        value={formik.values.tipoCartaFianza}
                                        options={tipoCartaFianzaList}
                                        placeholder="Selecciona"
                                        onChange={(val) => formik.setFieldValue("tipoCartaFianza", val)}
                                        isSearchable={false}
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-bold">Banco</Form.Label>
                                    <Select
                                        classNamePrefix='filter'
                                        components={animatedComponents}
                                        value={formik.values.banco}
                                        options={bancoOptions}
                                        placeholder="Selecciona"
                                        onChange={(val) => formik.setFieldValue("banco", val)}
                                        isSearchable={true}
                                        isLoading={isLoadingBancos}
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-bold">Importe</Form.Label>
                                    <ReactNumeric
                                        className="form-control"
                                        value={formik.values.importe}
                                        decimalCharacter="."
                                        digitGroupSeparator=","
                                        onChange={(e, val) => formik.setFieldValue("importe", val)}
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-md-6">
                                <Form.Group className="d-flex flex-column">
                                    <Form.Label className="fw-bold">Fecha Vencimiento Carta Fianza</Form.Label>
                                    <DatePicker
                                        className="form-control w-100"
                                        locale="es"
                                        selected={formik.values.endDate}
                                        dateFormat="dd/MM/yyyy"
                                        onChange={(date) => formik.setFieldValue("endDate", date)}
                                        showMonthDropdown={true}
                                        showYearDropdown={true}
                                        dropdownMode="select"
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        {/* Attachment PDF Zone */}
                        <div className="mt-4">
                            <h5 className="fw-bold mb-3">Archivo Adjunto (PDF)</h5>
                            <UploadZone
                                onDrop={customDrop}
                                fileUrl={urlAdjunto}
                                isLoading={isLoadingAdjunto}
                                fileAccept="pdf"
                            />
                        </div>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isLoadingSave}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={formik.submitForm} disabled={isLoadingSave || isFetchingData}>
                    {isLoadingSave ? 'Guardando...' : 'Guardar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
